import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'es' : 'en';
    i18n.changeLanguage(newLang);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      window.REACT_APP_NAVIGATE('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img
              src="https://static.readdy.ai/image/1c1efe20ab982279d0e71dac7f6c0a80/69489545e86907aa53371b0a163c3f48.png"
              alt="Art Throng Home"
              className="h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <button
              onClick={() => navigate('/services')}
              className="text-gray-700 hover:text-gray-900 font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('nav.services')}
            </button>
            <button
              onClick={() => navigate('/packages')}
              className="text-gray-700 hover:text-gray-900 font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('nav.packages')}
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-gray-700 hover:text-gray-900 font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('nav.about')}
            </button>
            <button
              onClick={() => scrollToSection('testimonials')}
              className="text-gray-700 hover:text-gray-900 font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('nav.testimonials')}
            </button>
          </div>

          {/* Right Section */}
          <div className="hidden lg:flex items-center space-x-6">
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 transition-colors cursor-pointer"
            >
              <i className="ri-global-line text-xl"></i>
              <span className="font-medium whitespace-nowrap">{i18n.language === 'en' ? 'ES' : 'EN'}</span>
            </button>
            <a
              href={`tel:${t('nav.phone')}`}
              className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 transition-colors cursor-pointer"
            >
              <i className="ri-phone-line text-xl"></i>
              <span className="font-medium whitespace-nowrap">{t('nav.phone')}</span>
            </a>
            <button
              onClick={() => scrollToSection('booking')}
              className="bg-[#8B9B7E] text-white px-6 py-3 rounded-full font-medium hover:bg-[#7a8a6d] transition-all flex items-center space-x-2 whitespace-nowrap cursor-pointer"
            >
              <span>{t('nav.bookNow')}</span>
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-gray-700 hover:text-gray-900 cursor-pointer"
          >
            <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden absolute top-20 left-0 right-0 bg-white shadow-lg py-4 px-6">
          <div className="flex flex-col space-y-4">
            <button
              onClick={() => {
                navigate('/services');
                setIsMobileMenuOpen(false);
              }}
              className="text-gray-700 hover:text-gray-900 font-medium transition-colors text-left cursor-pointer"
            >
              {t('nav.services')}
            </button>
            <button
              onClick={() => {
                navigate('/packages');
                setIsMobileMenuOpen(false);
              }}
              className="text-gray-700 hover:text-gray-900 font-medium transition-colors text-left cursor-pointer"
            >
              {t('nav.packages')}
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-gray-700 hover:text-gray-900 font-medium transition-colors text-left cursor-pointer"
            >
              {t('nav.about')}
            </button>
            <button
              onClick={() => scrollToSection('testimonials')}
              className="text-gray-700 hover:text-gray-900 font-medium transition-colors text-left cursor-pointer"
            >
              {t('nav.testimonials')}
            </button>
            <button
              onClick={() => scrollToSection('booking')}
              className="text-gray-700 hover:text-gray-900 font-medium transition-colors text-left cursor-pointer"
            >
              Booking
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-gray-700 hover:text-gray-900 font-medium transition-colors text-left cursor-pointer"
            >
              Contact
            </button>
            <button
              onClick={() => {
                toggleLanguage();
                setIsMobileMenuOpen(false);
              }}
              className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 transition-colors cursor-pointer pt-2 border-t border-gray-200"
            >
              <i className="ri-global-line text-xl"></i>
              <span className="font-medium">{i18n.language === 'en' ? 'Español' : 'English'}</span>
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}